import { supabase } from '@/lib/supabase';
import type {
  SupplierLocation,
  SupplierLocationView,
  CreateSupplierLocationInput,
  UpdateSupplierLocationInput,
} from '../types';
import { mapSupplierLocationFromDb, mapSupplierLocationViewFromDb } from '../types';

/**
 * Fetch all locations for a supplier using the v_supplier_locations view
 */
export async function fetchSupplierLocations(
  supplierId: string
): Promise<SupplierLocationView[]> {
  const { data, error } = await supabase
    .from('v_supplier_locations')
    .select('*')
    .eq('supplier_id', supplierId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[supplierLocationsApi] Error fetching locations:', error);
    throw new Error(error.message);
  }

  return (data || []).map(mapSupplierLocationViewFromDb);
}

/**
 * Fetch a single location by ID using the v_supplier_locations view
 */
export async function fetchLocationById(
  locationId: string
): Promise<SupplierLocationView | null> {
  const { data, error } = await supabase
    .from('v_supplier_locations')
    .select('*')
    .eq('id', locationId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('[supplierLocationsApi] Error fetching location:', error);
    throw new Error(error.message);
  }

  return mapSupplierLocationViewFromDb(data);
}

/**
 * Create a new supplier location
 */
export async function createLocation(
  input: CreateSupplierLocationInput
): Promise<SupplierLocation> {
  const { data, error } = await supabase
    .from('supplier_locations')
    .insert({
      business_id: input.business_id,
      supplier_id: input.supplier_id,
      location_name: input.location_name || null,
      street_address: input.street_address || null,
      unit: input.unit || null,
      city: input.city || null,
      province: input.province || null,
      postal_code: input.postal_code || null,
      country: input.country || 'Canada',
      phone: input.phone || null,
      email: input.email || null,
      contact_first_name: input.contact_first_name || null,
      contact_last_name: input.contact_last_name || null,
      notes: input.notes || null,
    })
    .select()
    .single();

  if (error) {
    console.error('[supplierLocationsApi] Error creating location:', error);
    throw new Error(error.message);
  }

  return mapSupplierLocationFromDb(data);
}

/**
 * Update an existing supplier location
 */
export async function updateLocation(
  locationId: string,
  input: UpdateSupplierLocationInput
): Promise<SupplierLocation> {
  // Build update object, only including defined fields
  const updateFields: Record<string, unknown> = {};

  if (input.location_name !== undefined) updateFields.location_name = input.location_name;
  if (input.street_address !== undefined) updateFields.street_address = input.street_address;
  if (input.unit !== undefined) updateFields.unit = input.unit;
  if (input.city !== undefined) updateFields.city = input.city;
  if (input.province !== undefined) updateFields.province = input.province;
  if (input.postal_code !== undefined) updateFields.postal_code = input.postal_code;
  if (input.country !== undefined) updateFields.country = input.country;
  if (input.phone !== undefined) updateFields.phone = input.phone;
  if (input.email !== undefined) updateFields.email = input.email;
  if (input.contact_first_name !== undefined) updateFields.contact_first_name = input.contact_first_name;
  if (input.contact_last_name !== undefined) updateFields.contact_last_name = input.contact_last_name;
  if (input.notes !== undefined) updateFields.notes = input.notes;

  const { data, error } = await supabase
    .from('supplier_locations')
    .update(updateFields)
    .eq('id', locationId)
    .select()
    .single();

  if (error) {
    console.error('[supplierLocationsApi] Error updating location:', error);
    throw new Error(error.message);
  }

  return mapSupplierLocationFromDb(data);
}

/**
 * Delete a supplier location
 */
export async function deleteLocation(locationId: string): Promise<void> {
  const { error } = await supabase
    .from('supplier_locations')
    .delete()
    .eq('id', locationId);

  if (error) {
    console.error('[supplierLocationsApi] Error deleting location:', error);
    throw new Error(error.message);
  }
}
